/**
 * MisconceptionChart — 오개념 누적 트래킹 차트
 *
 * useMisconceptions(unitId) 훅으로 progressStore의 오개념 기록을 읽어
 * 막대 차트 형태로 시각화합니다.
 *
 * TEST/ProblemPage에서 오개념이 기록될 때마다 자동 갱신됩니다.
 */

import React, { useState } from 'react'
import { AlertTriangle, Brain, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react'
import { useMisconceptions } from '../store/progressStore'

// 오개념별 색상 및 설명
const MISCONCEPTION_INFO: Record<string, {
  label: string
  color: string
  bg: string
  border: string
  desc: string
  tip: string
}> = {
  M01: {
    label: '기울기 오해',
    color: 'text-red-700',
    bg: 'bg-red-500',
    border: 'border-red-200',
    desc: '기울기를 단순 숫자로만 인식 — "변화율"의 의미를 놓침',
    tip: 'y = ax + b 에서 a는 x가 1 증가할 때 y의 변화량입니다.',
  },
  M02: {
    label: 'x/y 역할 혼동',
    color: 'text-orange-700',
    bg: 'bg-orange-500',
    border: 'border-orange-200',
    desc: '순서쌍 (x, y)에서 x와 y의 위치를 바꿔서 읽음',
    tip: '순서쌍은 항상 (x좌표, y좌표) 순서! x가 먼저입니다.',
  },
  M03: {
    label: '기울기·y절편 혼동',
    color: 'text-amber-700',
    bg: 'bg-amber-500',
    border: 'border-amber-200',
    desc: 'y = ax + b에서 a와 b의 위치를 바꿔서 읽음',
    tip: 'x 앞의 수(a)가 기울기, 상수항(b)이 y절편입니다.',
  },
  M04: {
    label: '정비례 ≠ 일차함수',
    color: 'text-yellow-700',
    bg: 'bg-yellow-500',
    border: 'border-yellow-200',
    desc: '정비례 함수(y=ax)를 일차함수가 아니라고 착각',
    tip: 'y = ax는 b = 0인 일차함수입니다. 정비례는 일차함수의 특수한 경우!',
  },
  M05: {
    label: '음수 기울기 오해',
    color: 'text-lime-700',
    bg: 'bg-lime-500',
    border: 'border-lime-200',
    desc: '기울기가 음수이면 함수가 아니라고 생각',
    tip: '기울기가 음수(a < 0)이면 오른쪽으로 갈수록 감소하는 직선입니다.',
  },
  M06: {
    label: '변화율 계산 오류',
    color: 'text-teal-700',
    bg: 'bg-teal-500',
    border: 'border-teal-200',
    desc: '표에서 기울기(변화율)를 잘못 계산 — Δy/Δx 순서 혼동',
    tip: '기울기 = Δy ÷ Δx = (y의 변화량) / (x의 변화량)',
  },
  M07: {
    label: '그래프↔식 변환 오류',
    color: 'text-cyan-700',
    bg: 'bg-cyan-500',
    border: 'border-cyan-200',
    desc: '그래프를 보고 식을 쓸 때 기울기나 절편을 잘못 읽음',
    tip: 'x절편과 y절편으로 두 점을 잡아 기울기 = (y₂−y₁)/(x₂−x₁)으로 계산하세요.',
  },
  M08: {
    label: '문장제 방향성 오류',
    color: 'text-purple-700',
    bg: 'bg-purple-500',
    border: 'border-purple-200',
    desc: '"감소/절약/냉각" 등의 단어를 보고 기울기 부호를 반대로 씀',
    tip: '"증가/상승/추가" → 양수 기울기, "감소/식음/소모" → 음수 기울기!',
  },
}

interface Props {
  unitId: string
  className?: string
}

export default function MisconceptionChart({ unitId, className = '' }: Props) {
  const misconceptions = useMisconceptions(unitId)
  const [expanded, setExpanded] = useState(false)

  const hasData = misconceptions.length > 0
  const maxHit = hasData ? Math.max(...misconceptions.map(m => m.hitCount)) : 1

  if (!hasData) {
    return (
      <div className={`bg-emerald-50 border border-emerald-200 rounded-2xl p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <Brain size={16} className="text-emerald-500" />
          <h3 className="font-bold text-emerald-700 text-sm">오개념 트래킹</h3>
        </div>
        <p className="text-xs text-emerald-600">
          🎉 아직 기록된 오개념이 없습니다. 문제풀이 탭과 실전 TEST를 풀면 자동으로 분석됩니다!
        </p>
      </div>
    )
  }

  const visibleItems = expanded ? misconceptions : misconceptions.slice(0, 4)

  return (
    <div className={`bg-white border border-slate-200 rounded-2xl p-4 ${className}`}>
      {/* 헤더 */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
          <AlertTriangle size={14} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-800 text-sm">오개념 누적 분석</h3>
          <p className="text-xs text-slate-400">틀린 문제에서 감지된 오개념 패턴</p>
        </div>
        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold">
          {misconceptions.length}개 유형
        </span>
      </div>

      {/* 차트 */}
      <div className="space-y-2.5">
        {visibleItems.map((m, idx) => {
          const info = MISCONCEPTION_INFO[m.code] ?? {
            label: m.label, color: 'text-slate-700', bg: 'bg-slate-500',
            border: 'border-slate-200', desc: '', tip: '',
          }
          const pct = Math.round((m.hitCount / maxHit) * 100)
          const severity = m.hitCount >= 3 ? 'high' : m.hitCount >= 2 ? 'medium' : 'low'

          return (
            <div key={m.code}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-black px-1.5 py-0.5 rounded ${
                  severity === 'high'
                    ? 'bg-red-100 text-red-600'
                    : severity === 'medium'
                      ? 'bg-orange-100 text-orange-600'
                      : 'bg-slate-100 text-slate-600'
                }`}>
                  {m.code}
                </span>
                <span className={`text-xs font-semibold ${info.color}`}>{info.label}</span>
                <span className="ml-auto text-xs font-black text-slate-600">
                  {m.hitCount}회
                  {severity === 'high' && <span className="ml-1 text-red-500">⚠️</span>}
                </span>
              </div>
              {/* 막대 */}
              <div className="bg-slate-100 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-700 ${info.bg}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {/* 팁 */}
              {expanded && info.tip && (
                <p className="text-xs text-slate-500 mt-1 pl-1">💡 {info.tip}</p>
              )}
            </div>
          )
        })}
      </div>

      {/* 더보기/접기 */}
      {misconceptions.length > 4 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 w-full text-xs text-slate-400 hover:text-slate-600 flex items-center justify-center gap-1 transition-colors"
        >
          {expanded
            ? <><ChevronUp size={12} /> 접기</>
            : <><ChevronDown size={12} /> {misconceptions.length - 4}개 더 보기</>
          }
        </button>
      )}

      {/* 설명 토글 */}
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="mt-2 w-full text-xs text-blue-500 hover:text-blue-700 flex items-center justify-center gap-1 transition-colors"
        >
          <TrendingDown size={11} /> 상세 분석 보기
        </button>
      )}

      {/* 경고 문구 */}
      {misconceptions.some(m => m.hitCount >= 3) && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-2.5 flex items-start gap-2">
          <AlertTriangle size={13} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-600">
            <span className="font-bold">집중 복습 필요!</span>{' '}
            {misconceptions.filter(m => m.hitCount >= 3).map(m => m.code).join(', ')} 오개념이 반복되고 있습니다.
            개념 학습 탭에서 해당 단계를 다시 학습하세요.
          </p>
        </div>
      )}
    </div>
  )
}
